import styled from 'styled-components'
import { COLOR, GTR } from '@farewill/ui/tokens'

type TableProps = {
  children: React.ReactNode
  striped?: boolean
  withHeaders?: boolean
  /** Gives every row the same side padding, so a highlighted row has room
   * and its columns still line up with the rest */
  padded?: boolean
}

const StyledTable = styled.div<{
  $striped?: boolean
  $withHeaders?: boolean
  $padded?: boolean
}>`
  border-width: 1px 0;
  border-style: solid;
  border-color: ${COLOR.GREY.LIGHT};

  ${({ $withHeaders }) =>
    $withHeaders &&
    `
    border-top: none;
  `}

  ${({ $padded }) =>
    $padded &&
    `
      > div {
        padding-left: ${GTR.S};
        padding-right: ${GTR.S};
      }
    `}

  ${({ $striped, $withHeaders }) =>
    $striped &&
    `
      > div:nth-child(${$withHeaders ? '2n' : '2n - 1'}) {
        background-color: ${COLOR.BACKGROUND.SMOKE};
      }
    `}
`

const StyledRow = styled.div<{
  $gridTemplateCols: string
  $highlight?: boolean
  $clickable?: boolean
}>`
  display: grid;
  grid-template-columns: ${({ $gridTemplateCols }) => $gridTemplateCols};
  grid-column-gap: ${GTR.L};
  padding: ${GTR.S} 0;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${COLOR.GREY.LIGHT};
  }

  ${({ $highlight }) =>
    $highlight && `background-color: ${COLOR.STATE.SUCCESS_10};`}

  ${({ $clickable }) => $clickable && `cursor: pointer;`}
`

const StyledCol = styled.div<{ $strong?: boolean; $span?: number }>`
  white-space: pre-wrap;
  ${({ $strong }) => $strong && `font-weight: bold;`}
  ${({ $span }) => $span && `grid-column: span ${$span};`}
`

const Table = ({
  children,
  striped,
  withHeaders,
  padded,
}: TableProps): React.ReactElement => {
  return (
    <StyledTable $striped={striped} $withHeaders={withHeaders} $padded={padded}>
      {children}
    </StyledTable>
  )
}

type RowProps = {
  children: React.ReactNode
  gridTemplateCols: string
  /** Sets the row apart from the rows around it */
  highlight?: boolean
  /** Makes the whole row clickable */
  onClick?: () => void
  'data-testid'?: string
}

const Row = ({
  children,
  gridTemplateCols,
  highlight,
  onClick,
  'data-testid': testId,
}: RowProps): React.ReactElement => {
  return (
    <StyledRow
      $gridTemplateCols={gridTemplateCols}
      $highlight={highlight}
      $clickable={Boolean(onClick)}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </StyledRow>
  )
}

type ColProps = {
  children: React.ReactNode
  strong?: boolean
  /** How many of the row's columns the cell spans */
  span?: number
}

const Col = ({ children, strong, span }: ColProps): React.ReactElement => {
  return (
    <StyledCol $strong={strong} $span={span}>
      {children}
    </StyledCol>
  )
}

Table.Row = Row
Table.Col = Col

export default Table
